/* eslint-disable @typescript-eslint/no-unused-vars */
import { Badge, Select, Space, Table } from "antd";
import { SideDrawerWrapper } from "components/Global";

import { ChangeEvent, FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getActiveConnectionAction } from "redux/action/activeConnectionsActions";
import { getApiLogs } from "redux/action/apiLogsAction";
import { getApiKey } from "redux/action/apikeyAction";
import { AppDispatch } from "redux/store";
import { formatUrl } from "utils/Common";
import { pageSizeOptionsPaging } from "utils/constant";
import { SortSvgBottom, SortSvgTop } from "utils/svgs";
import { formatDate } from "utils/utils";
import APIKeyDetailedRequestDrawer from "../APIKeyDetailedRequest";
import LogsSearchAndFilter from "../SearchAndFilter";
import "./index.scss";
import { DynamicLogsTableProps } from "./type";

const SortingIcon = (data: any) => {
  return data.data.sortOrder === "ascend" ? <SortSvgTop /> : <SortSvgBottom />;
};

const DynamicLogsTable: FC<DynamicLogsTableProps> = (props) => {
  const { Column } = Table;
  const { logsDataSource, isLoading, totalRecords, setLoadingHandler } = props;
  const [drawerAnimation, setDrawerAnimation] = useState<boolean>(false);
  const [isSideDrawerOpen, setSideDrawerOpen] = useState<boolean>(false);
  const [tableData, setTableData] = useState(logsDataSource);
  const [drawerInfo, setDrawerInfo] = useState({ drawerTitle: "" });
  const [drawerData, setDrawerData] = useState({});
  const [searchValue, setSearchValue] = useState("");
  const [pageSize, setPageSize] = useState(20);
  const [currentPageNo, setCurrentPageNo] = useState(1);
  const [connections, setConnections] = useState<any>([]);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const activeServices = useSelector((state: any) => state.ActiveServices.data);
  const [sortDirection, setSortDirection] = useState("desc");
  const [entity, setEntity] = useState<string>("");
  // const [entityTableColumns] = useState<any>();
  const [filterValue, setFilterValue] = useState<any | null>({
    filterFieldName: null,
    filterFieldValue: null,
    filterFieldValue2: null,
  });
  const [onRequestResent, setOnRequestResent] = useState({})
  const dispatch = useDispatch<AppDispatch>();

  const apiLogData = useSelector(
    (data: any) => data?.apilogs?.data?.data?.length
  );

  const fetchApiLogs = () => {
    setLoadingHandler(true);

    const tenantId = localStorage.getItem("companyId");

    if (!tenantId) {
      setLoadingHandler(false);
      return;
    }
    const query: any = {
      page: currentPageNo,
      limit: pageSize,
      search: searchValue,
      filter: filterValue,
      type: sortDirection,
      entityName: entity || "",
    };

    if (selectedCompany?.id) {
      query.companyId = selectedCompany.id;
    }
    if (filterValue === " ") {
      delete query.filter;
    }

    dispatch(getApiLogs(query))
      .then((response: any) => {
        if (response?.payload?.data) {
          const formattedData = response.payload.data.map((item: any) => ({
            ...item,
            createdAt: formatDate(item?.createdAt),
          }));
          setTableData(formattedData);
        } else {
          setTableData([]);
        }
        setLoadingHandler(false);
      })
      .catch((error: any) => {
        console.error("Error fetching API logs:", error);
        setTableData([]);
        setLoadingHandler(false);
      });
  };

  const handleSelectCompany = (value: string) => {
    if (value === "") {
      localStorage.removeItem("connectedPlayground");
      setSelectedCompany({ label: "Select", value: "" });
    } else {
      const selectedComp = activeServices?.find(
        (connection: any) => connection?.id === value
      );
      if (selectedComp) {
        localStorage.setItem(
          "connectedPlayground",
          JSON.stringify({
            channelName: selectedComp.channelName,
            companyName: selectedComp.companyName,
            id: selectedComp.id,
          })
        );
        setSelectedCompany(selectedComp);
      }
    }

    setCurrentPageNo(1);
    fetchApiLogs(); // Fetch data based on selection
  };

  const handleEntityChange = (value: string) => {
    if (value === "") {
      setEntity("");
    } else {
      setEntity(value);
    }
    setCurrentPageNo(1);
  };

  const openSideBarDrawer = (data: any) => {
    setDrawerData(data);
    setDrawerInfo({ drawerTitle: "Request Details" });
    setDrawerAnimation(true);
    setSideDrawerOpen(true);
  };

  const removeDrawerFromDom = () => {
    setSideDrawerOpen(false);
  };

  const closeDrawerByAnimation = () => {
    setDrawerAnimation(false);
  };

  const performSearchHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchValue(value);
    setCurrentPageNo(1);
  };

  const performFilterHandler = (value: string) => {
    setFilterValue(value);
  };

  // Modify pageSize
  const modifyPageSize = (current: number, size: number) => {
    if (!size) {
      setPageSize(10);
    } else {
      setPageSize(size);
    }
    setCurrentPageNo(1);
  };

  const paginationChangeHandler = (pageNo: number, pageSize: number) => {
    setCurrentPageNo(pageNo);
    setPageSize(pageSize);
  };

  const tableChangeHandler = (pageInfo: any, d: any, columnInfo: any) => {
    const orderVal = columnInfo.order === "descend" ? "desc" : "asc";
    setSortDirection(orderVal);
    setCurrentPageNo(pageInfo.current);
  };

  useEffect(() => {
    fetchApiLogs();
  }, [
    selectedCompany,
    entity,
    pageSize,
    currentPageNo,
    filterValue,
    searchValue,
    sortDirection,
  ]);

  useEffect(() => {
    if (activeServices?.length) {
      const _connections = [
        { label: "All", value: "" },
        ...activeServices.map((connection: any) => ({
          label: `${connection.companyName} (${connection.channelName})`,
          value: connection.id,
        })),
      ];

      setConnections(_connections);

      const connectedPlaygroundData = JSON.parse(
        localStorage.getItem("connectedPlayground") || "{}"
      );

      if (connectedPlaygroundData?.id) {
        setSelectedCompany(connectedPlaygroundData);
      } else {
        setSelectedCompany({ label: "All", value: "" });
      }
    }
  }, [activeServices]);

  useEffect(() => {
    if (apiLogData === 0 && currentPageNo > 1) {
      setCurrentPageNo(currentPageNo - 1);
    }
  }, [apiLogData]);

  useEffect(() => {
    dispatch(getActiveConnectionAction());
    dispatch(getApiKey());
    setSelectedCompany({ label: "--Select Company--", value: "" });
    localStorage.removeItem("connectedPlayground");
  }, []);

  useEffect(() => {
    if (selectedCompany?.id) {
      fetchApiLogs();
    }
  }, [
    selectedCompany,
    entity,
    pageSize,
    currentPageNo,
    filterValue,
    searchValue,
    sortDirection,
  ]);

  const handleRequestResent = (data: any) => {
    console.log("Resent request response:", data);
    // You can update state or trigger side effects here
  };

  useEffect(() => {
    if (apiLogData === 0 && currentPageNo > 1) {
      setCurrentPageNo(currentPageNo - 1);
    }
  }, [apiLogData]);

  const statusOptions = [
    { value: "200", label: "OK", color: "#52c41a" },
    { value: "400", label: "Bad Request", color: "#faad14" },
    { value: "404", label: "Not Found", color: "#456EFB" },
    { value: "502", label: "Bad Gateway", color: "red" },
  ];

  return (
    <div className={"dynamic-table"}>
      <div className="search-filter-container">
        <div className="search-company-filter">
          <Select
            className="company-select"
            placeholder="Select Company"
            options={connections}
            onChange={handleSelectCompany}
            value={selectedCompany?.id || ""}
            style={{ width: 250 }}
            size="large"
          />

          <Select
            className="entity-select"
            placeholder="Select Entity"
            size="large"
            options={[
              { label: "All", value: "" },
              { label: "Customers", value: "customer" },
              { label: "Accounts", value: "account" },
              { label: "Vendors", value: "vendor" },
              { label: "Journal", value: "journal" },
              { label: "Expense", value: "expense" },
              { label: "Bill Payment", value: "bill-payment" },
              { label: "Invoice", value: "invoice" },
            ]}
            onChange={handleEntityChange}
            value={entity}
            style={{ width: 200 }}
          />
        </div>

        <LogsSearchAndFilter
          performSearchHandler={performSearchHandler}
          searchValue={searchValue}
          performFilterHandler={performFilterHandler}
        />
      </div>
      <Table
        dataSource={tableData}
        scroll={{ y: "calc(70vh - 133px)" }}
        pagination={{
          total: totalRecords,
          current: currentPageNo,
          onChange: paginationChangeHandler,
          pageSize: pageSize,
          pageSizeOptions: pageSizeOptionsPaging,
          showSizeChanger: true,
          onShowSizeChange: modifyPageSize,
        }}
        loading={isLoading}
        // entityTableColumns={entityTableColumns}
        className="table-global tableHover"
        onChange={tableChangeHandler}
        style={{ cursor: "pointer" }}
        onRow={(record: any) => {
          return {
            onClick: () => {
              openSideBarDrawer(record);
            },
          };
        }}
      >
        <Column
          title="Methods"
          dataIndex="method"
          key="method"
          width={"10%"}
          className="bg-white"
        />
        <Column
          title="URL"
          dataIndex="apiUrl"
          key="apiUrl"
          className="bg-white "
          width={"13%"}
          render={(value: string) => {
            return (
              <>
                <Space>{formatUrl(value)}</Space>
              </>
            );
          }}
        />

        <Column
          title="Code | Status"
          dataIndex="status"
          key="status"
          className="bg-white badge-icon"
          width={"11%"}
          filteredValue={
            filterValue?.filterFieldName === "status"
              ? [filterValue.filterFieldValue]
              : null
          }
          onFilter={(value, record) => record.status === value}
          render={(value: string, record: any) => {
            const matchedStatus = statusOptions.find(
              (s) => s.value == value
            ) || {
              label: "Unauthorized",
              color: "#91a3b0",
            };
            return (
              <Space>
                <Badge color={matchedStatus.color} />
                <span className="dynamic-table__status-text">
                  {value} | {matchedStatus.label}
                </span>
              </Space>
            );
          }}
        />

        <Column
          title="Integration"
          dataIndex="Integration"
          key="Integration"
          className="bg-white"
          width={"13%"}
          render={(value: string) => {
            return (
              <>
                <Space style={value === null ? { paddingLeft: "40px" } : {}}>
                  {value === null ? "-" : value}
                </Space>
              </>
            );
          }}
        />
        <Column
          title="Timestamp"
          dataIndex="createdAt"
          key="createdAt"
          className="bg-white"
          width={"14%"}
          showSorterTooltip={{ title: "" }}
          sorter={() => {
            return null as any;
          }}
          sortDirections={["ascend", "descend", "ascend"]}
          sortIcon={(data) => <SortingIcon data={data} />}
        />
      </Table>

      {isSideDrawerOpen && (
        <SideDrawerWrapper
          isOpen={drawerAnimation}
          removeDrawerFromDom={removeDrawerFromDom}
          closeDrawerByAnimation={closeDrawerByAnimation}
          headerTitle={drawerInfo.drawerTitle}
          position="right"
          width="half"
        >
          <APIKeyDetailedRequestDrawer drawerData={drawerData}
            onRequestResent={handleRequestResent}
          />
        </SideDrawerWrapper>
      )}
    </div>
  );
};

export default DynamicLogsTable;
